import { createContext, useState, useContext } from "react";
import CRMService from "../services/crm.service";

// Count for all nav categories and subcategories
const buildCounts = (items) => {
  const counts = {
    guest: 0,
    owner: 0,
    leads: 0,
    housekeeper: 0,
    maintenance: 0,
    vendor: 0,
    other: 0,
    unit: 0,
  };
  for (const item of items) {
    if (item._type === "unit") {
      counts.unit++;
      continue;
    }
    switch (item.category) {
      case "guest":
        counts.guest++;
        break;
      case "owner":
        counts.owner++;
        break;
      case "lead":
      case "leads":
        counts.leads++;
        break;
      case "housekeeper":
        counts.housekeeper++;
        break;
      case "maintenance":
        counts.maintenance++;
        break;
      case "vendor":
        counts.vendor++;
        break;
      case "other":
      default:
        counts.other++;
        break;
    }
  }
  return counts;
};

export const CRMContext = createContext();
export const CRMProvider = ({ children }) => {
  const [flagIsProcessing, setIsProcessing] = useState(false);
  const [flagIsEditing, setIsEditing] = useState(false);
  const [flagIsNew, setIsNew] = useState(false);
  const [listContacts, setContacts] = useState([]);
  const [contact, setContact] = useState(null);
  const [totalCnt, setTotalCnt] = useState(0);
  const [activeFilter, setActiveFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [counts, setCounts] = useState({ all: 0, guest: 0, owner: 0, leads: 0 });

  // Batch operations
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [batchFlag, setBatchFlag] = useState(false);

  // Timeline
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Activity off-canvas state
  const [activityTarget, setActivityTarget] = useState(null);
  const [activityTargetType, setActivityTargetType] = useState(null);

  const contextCRM = {
    flagIsProcessing,
    flagIsEditing,
    setIsEditing,
    flagIsNew,
    setIsNew,
    listContacts,
    contact,
    setContact,
    totalCnt,
    activeFilter,
    setActiveFilter,
    statusFilter,
    setStatusFilter,
    counts,
    selectedContacts,
    setSelectedContacts,
    batchFlag,
    setBatchFlag,
    timeline,
    timelineLoading,
    activityTarget,
    activityTargetType,

    setActivityTarget: (target, type) => {
      setActivityTarget(target);
      setActivityTargetType(type);
    },

    initContact: () => {
      setContact({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        category: "owner",
        tags: [],
        rentals: [],
        notes: "",
        co_owners: [],
      });
    },

    /**
     * Build the unified CRM list by fetching real contacts from the API.
     * @param {Object} criteria – current filter/search criteria
     */
    buildCRMList: async (criteria) => {
      setIsProcessing(true);
      let realContacts = [];
      let guests = [];
      let units = [];
      let fetchCategory = criteria.category;

      try {
        if (fetchCategory === "guest") {
          // Guests come from the guest table, not contacts
          const guestCriteria = {
            search_term: criteria.search_term || "",
            sort: criteria.sort || "updated_at",
            per: criteria.per || "20",
            page: criteria.page || 0,
          };
          const res = await CRMService.searchGuests(guestCriteria);
          guests = res.data.paginated || res.data || [];
        } else if (fetchCategory !== "unit") {
          const contactsCriteria = { ...criteria };
          delete contactsCriteria.category;
          delete contactsCriteria.units;
          if (fetchCategory === "leads") {
            contactsCriteria.type = "leads";
          } else if (fetchCategory && fetchCategory !== "all") {
            contactsCriteria.type = fetchCategory;
          }
          const res = await CRMService.searchContacts(contactsCriteria);
          realContacts = res.data.paginated || res.data || [];
        }
      } catch (err) {
        console.error("CRM: Failed to fetch contacts", err);
      }

      // Units (properties) passed in from the CRM page
      if (criteria.units && Array.isArray(criteria.units)) {
        units = criteria.units.map((r) => ({ ...r, _type: "unit", id: r.rental_id || r.pms_id }));
      }

      // Normalise contacts
      // IMPORTANT: `id` MUST be the contacts.id PK (returned by the API as
      // `contact_id`), not the crm_identity row id. The contacts edit/delete
      // endpoints (`PUT/DELETE /contacts/:id`) look up the contacts table
      // directly, so leaking a crm_identity_id here would cause edits/deletes
      // to silently target the wrong row (or 404).
      const normalised = (realContacts || [])
        .filter((c) => c && c.contact_id != null)
        .map((c) => ({
          ...c,
          id: c.contact_id,
          crm_identity_id: c.crm_identity_id || c.id,
          category: c.category === "leads" ? "lead" : c.category,
          formatted_phone: c.formatted_phone || c.phone_e164 || c.phone || "",
          activities: c.activities || [],
          reservations: c.reservations || [],
          tags: c.tags || [],
          rentals: c.rentals || [],
          co_owners: c.co_owners || [],
        }));

      // Normalise guests from the guest table
      const normalisedGuests = (guests || []).map((g) => ({
        ...g,
        _type: "guest",
        // Some guests are linked via guest_reservation rather than the guest
        // table; fall back to the crm identity id so downstream callers always
        // have a stable, resolvable id.
        id: g.guest_id || g.crm_identity_id || g.id,
        category: "guest",
        first_name: g.first_name || "",
        last_name: g.last_name || "",
        formatted_phone: g.formatted_phone || g.phone || "",
      }));

      // Combine based on category filter
      let combined = [];
      let totalCount = 0;
      if (criteria.category === "unit") {
        combined = units;
        // Apply search filter for units
        if (criteria.search_term) {
          const term = criteria.search_term.toLowerCase();
          combined = combined.filter(
            (c) =>
              (c.pms_id && c.pms_id.toLowerCase().includes(term)) ||
              (c.name && c.name.toLowerCase().includes(term)) ||
              (c.address && c.address.toLowerCase().includes(term)),
          );
        }
        // Tag filter for units
        if (criteria.tag) {
          combined = combined.filter((u) => (u.tags || []).some((t) => (t.name || t) === criteria.tag));
        }
        // Sort units
        const sortKey = criteria.sort || "updated_at";
        combined = [...combined].sort((a, b) => {
          if (sortKey === "updated_at") {
            return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
          }
          const av = (a[sortKey] || "").toString().toLowerCase();
          const bv = (b[sortKey] || "").toString().toLowerCase();
          return av.localeCompare(bv);
        });
        totalCount = combined.length;
      } else if (criteria.category === "guest") {
        combined = normalisedGuests;
        totalCount = guests.length || combined.length;
      } else {
        combined = normalised;
        totalCount = realContacts.length || combined.length;
      }

      setContacts(combined);
      setTotalCnt(totalCount);

      // Refresh the currently-selected contact with the latest data so
      // detail panel reflects updates (e.g. tag changes from bulk actions).
      setContact((prev) => {
        if (!prev) return prev;
        const prevKey = prev.id || prev.rental_id;
        const fresh = combined.find(
          (c) => (c.id || c.rental_id) === prevKey,
        );
        return fresh ? { ...prev, ...fresh } : prev;
      });

      // Recalculate counts: fetch all contacts for total counts
      let allContacts = normalised;
      if (fetchCategory && fetchCategory !== "all" && fetchCategory !== "unit") {
        try {
          const countRes = await CRMService.searchContacts({
            search_term: "",
            sort: "updated_at",
            per: "10000",
            page: 0,
          });
          const countData = countRes.data.paginated || countRes.data || [];
          allContacts = (countData || []).map((c) => ({
            ...c,
            id: c.contact_id || c.id,
            category: c.category === "leads" ? "lead" : c.category,
          }));
        } catch (err) {
          // Fall back to what we have
        }
      }
      const allForCounts = [...allContacts, ...units];
      const computedCounts = buildCounts(allForCounts);

      // Fetch guest count from the guest table
      try {
        const guestCountRes = await CRMService.searchGuests({
          search_term: "",
          sort: "updated_at",
          per: "1",
          page: 0,
        });
        computedCounts.guest = parseInt(guestCountRes.data.total, 10) || 0;
      } catch (err) {
        // Fall back — leave guest count as-is
      }

      setCounts(computedCounts);
      setIsProcessing(false);
    },

    updateItemInList: (id, updates) => {
      setContacts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      );
      setContact((prev) => (prev && prev.id === id ? { ...prev, ...updates } : prev));
    },

    loadTimeline: async (contactId) => {
      if (!contactId) return;
      setTimelineLoading(true);
      try {
        const res = await CRMService.getContactTimeline(contactId);
        setTimeline(res.data || []);
      } catch (err) {
        console.error("CRM: Failed to fetch timeline", err);
        setTimeline([]);
      }
      setTimelineLoading(false);
    },

    addActivity: async (contactId, activity) => {
      // Persist the activity as a note via API
      try {
        const notePayload = {
          item_id: contactId,
          item_type: "contact",
          content: activity.description,
          note_type: activity.type, // 'note', 'phone_call', 'task'
        };
        await CRMService.createNote(notePayload);
        // Refresh timeline
        const res = await CRMService.getContactTimeline(contactId);
        setTimeline(res.data || []);
      } catch (err) {
        console.error("CRM: Failed to add activity", err);
        // Fallback: add locally
        const newActivity = {
          id: Date.now(),
          ...activity,
          created_at: new Date().toISOString(),
        };
        setTimeline((prev) => [newActivity, ...prev]);
      }
    },

    insertContact: async (objContact) => {
      try {
        const res = await CRMService.insertContact(objContact);
        if (res.data) {
          setContacts((prev) => [res.data, ...prev]);
          setTotalCnt((prev) => +prev + 1);
          return res.data;
        }
      } catch (err) {
        console.error("CRM: Failed to insert contact", err);
      }
    },

    updateContact: async (objContact) => {
      try {
        const res = await CRMService.updateContact(objContact);
        if (res.data) {
          setContacts((prev) =>
            prev.map((item) =>
              (item.id || item.contact_id) === (res.data.id || res.data.contact_id)
                ? { ...item, ...res.data }
                : item,
            ),
          );
          setContact((prev) => prev ? { ...prev, ...res.data } : res.data);
          return res.data;
        }
      } catch (err) {
        console.error("CRM: Failed to update contact", err);
      }
    },

    deleteContact: async (objContact) => {
      try {
        await CRMService.deleteContact(objContact);
        setContacts((prev) =>
          prev.filter((item) => item.id !== objContact.id),
        );
        setTotalCnt((prev) => prev - 1);
        if (contact?.id === objContact.id) {
          setContact(null);
        }
      } catch (err) {
        console.error("CRM: Failed to delete contact", err);
      }
    },

    importContacts: async (contactsArr) => {
      try {
        const res = await CRMService.importContacts(contactsArr);
        if (res.data?.saved) {
          setContacts((prev) => [...res.data.saved, ...prev]);
          setTotalCnt((prev) => prev + (res.data.saved || []).length);
        }
        return res.data;
      } catch (err) {
        console.error("CRM: Failed to import contacts", err);
      }
    },

    sendMessageToContacts: async (contacts, message) => {
      try {
        const res = await CRMService.sendMessageToContacts({
          contacts,
          msg: message,
        });
        return res?.data;
      } catch (err) {
        console.error("CRM: Failed to send message", err);
      }
    },
  };

  return (
    <CRMContext.Provider value={contextCRM}>
      {children}
    </CRMContext.Provider>
  );
};
